/**
 * Browser-safe AI Connections allowlist copy.
 *
 * Display metadata only — no commands, paths, or probe argv.
 * Must stay in sync with apps/brunel-node/catalog/ai-connections.json keys.
 */

export type AiConnectionMeterMode = 'percent' | 'spend' | 'unknown' | 'local_unlimited';
export type AiConnectionMode = 'commandos_vault' | 'node_bound' | 'local_no_auth' | 'explicit_api_key';

export interface AiConnectionDisplay {
  readonly connectionKey: string;
  readonly displayLabel: string;
  readonly meterLabel: string;
  readonly meterMode: AiConnectionMeterMode;
  readonly providerKey: string;
  readonly poolKey: string;
  /** Runtime keys this connection can safely authenticate on one node. */
  readonly runtimeKeys: readonly string[];
  readonly canConnect: boolean;
  readonly canRefreshUsage: boolean;
  readonly defaultNote: string | null;
  readonly connectionMode: AiConnectionMode;
}

/** Closed allowlist mirrored for Settings UI. */
export const AI_CONNECTIONS_DISPLAY: readonly AiConnectionDisplay[] = [
  {
    connectionKey: 'openai-codex',
    displayLabel: 'Codex CLI',
    meterLabel: 'Codex subscription',
    meterMode: 'percent',
    providerKey: 'openai',
    poolKey: 'codex-chatgpt',
    runtimeKeys: ['codex-cli'],
    canConnect: true,
    canRefreshUsage: true,
    defaultNote: 'Executable Codex runtime only; regular ChatGPT bridge status is separate.',
    connectionMode: 'node_bound',
  },
  {
    connectionKey: 'anthropic-claude',
    displayLabel: 'Claude Code',
    meterLabel: 'Claude Code',
    meterMode: 'unknown',
    providerKey: 'anthropic',
    poolKey: 'claude-subscription',
    runtimeKeys: ['claude-code'],
    canConnect: true,
    canRefreshUsage: true,
    defaultNote: 'Connected · subscription allowance not exposed by provider',
    connectionMode: 'node_bound',
  },
  {
    connectionKey: 'xai-grok-build',
    displayLabel: 'Grok Build',
    meterLabel: 'Grok shared weekly pool',
    meterMode: 'percent',
    providerKey: 'xai',
    poolKey: 'grok-weekly',
    runtimeKeys: ['grok-build'],
    canConnect: true,
    canRefreshUsage: true,
    defaultNote: null,
    connectionMode: 'node_bound',
  },
  {
    connectionKey: 'grok-bot',
    displayLabel: 'Grok Bot',
    meterLabel: 'Grok Bot',
    meterMode: 'unknown',
    providerKey: 'xai',
    poolKey: 'grok-bot',
    runtimeKeys: [],
    canConnect: false,
    canRefreshUsage: true,
    defaultNote: 'Connected/installed · usage API unavailable',
    connectionMode: 'node_bound',
  },
  {
    connectionKey: 'ollama-local',
    displayLabel: 'Ollama Local',
    meterLabel: 'Ollama Local',
    meterMode: 'local_unlimited',
    providerKey: 'ollama',
    poolKey: 'local',
    runtimeKeys: ['ollama'],
    canConnect: false,
    canRefreshUsage: true,
    defaultNote: 'Local · Unlimited',
    connectionMode: 'local_no_auth',
  },
  {
    connectionKey: 'ollama-cloud',
    displayLabel: 'Ollama Cloud',
    meterLabel: 'Ollama Cloud',
    meterMode: 'unknown',
    providerKey: 'ollama',
    poolKey: 'cloud',
    runtimeKeys: ['ollama'],
    canConnect: true,
    canRefreshUsage: true,
    defaultNote: 'Connected · account balance not exposed by provider',
    connectionMode: 'node_bound',
  },
];

export const AI_CONNECTION_KEYS: readonly string[] = AI_CONNECTIONS_DISPLAY.map((row) => row.connectionKey);

export function isAllowedConnectionKey(value: string): boolean {
  return AI_CONNECTION_KEYS.includes(value);
}
